import { Phone, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { ligarOutroNumeroAutoDialer } from '@/lib/autoDialerApi';
import { useAutoDialerSessao, useIniciarAutoDialer } from '@/hooks/useAutoDialer';
import { useNumerosParaLigar } from '@/hooks/useNumerosParaLigar';
import type { NumeroParaLigar } from '@/lib/numerosParaLigarApi';

/**
 * NÚMEROS PARA LIGAR (Douglas 05–06/10/2026, desenho aprovado).
 *
 * Embaixo da arte da proposta, em todo lead: todos os celulares do cliente, na ordem
 * do Auto Dialer — contato real, representante, dono, sócios e o resto com WhatsApp
 * primeiro. O telefone da linha é a ligação manual de hoje (softphone, pela Linx).
 * "É o contato real" leva o número para o formulário do Contato, como em Complementar.
 */

const CARD = 'rounded-xl border border-border bg-card shadow-[0_1px_2px_rgba(60,20,30,0.05),0_4px_14px_-6px_rgba(60,20,30,0.08)]';
const HACHURA = 'bg-[repeating-linear-gradient(135deg,#5B0A0D_0_3px,#B4808C_3px_6px)] dark:bg-[repeating-linear-gradient(135deg,#C24545_0_3px,#5E2229_3px_6px)]';

const fmt = (t: string) => (t.length === 11 ? `(${t.slice(0, 2)}) ${t.slice(2, 7)}-${t.slice(7)}` : t);
const diaMes = (s: string) => new Date(s).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'America/Sao_Paulo' });

function operadora(o: string | null): string | null {
  const u = String(o || '').toUpperCase();
  if (!u) return null;
  if (u.startsWith('CLARO')) return 'Claro';
  if (u.startsWith('VIVO') || u.startsWith('TELEF')) return 'Vivo';
  if (u.startsWith('TIM')) return 'TIM';
  if (u.startsWith('OI')) return 'Oi';
  if (u.startsWith('ALGAR')) return 'Algar';
  return u.charAt(0) + u.slice(1).toLowerCase();
}

const DESFECHO: Record<string, string> = {
  atendida: 'atendeu', caixa_postal: 'caixa postal', nao_atende: 'não atendeu', ocupado: 'ocupado',
  invalido: 'número inválido', rejeitada: 'recusou',
};

function ultimaVez(n: NumeroParaLigar): string {
  if (!n.ultima_ligacao) return 'nunca ligamos';
  const o = DESFECHO[String(n.ultimo_desfecho || '')];
  return `${o ?? 'ligamos'} ${diaMes(n.ultima_ligacao)}`;
}

export function NumerosParaLigarCard({ leadId, ativo, onLigar, onContatoReal, sequencia }: {
  leadId: string;
  ativo: boolean;
  onLigar: (telefone: string) => void;
  onContatoReal: (telefone: string) => void;
  /** Este lead e os próximos da lista aberta, na ordem das setas (o Auto Dialer segue para eles). */
  sequencia?: string[];
}) {
  const { data, isLoading, isError, error, refetch } = useNumerosParaLigar(leadId, ativo);
  const autoDialer = useAutoDialerSessao(ativo);
  const iniciar = useIniciarAutoDialer();
  const adAberto = autoDialer.data?.sessao && autoDialer.data.sessao.status !== 'encerrada' ? autoDialer.data : null;
  const fila = sequencia && sequencia[0] === leadId ? sequencia : [leadId, ...(sequencia ?? []).filter((id) => id !== leadId)];
  const depois = fila.length - 1;
  const numeros = data?.numeros ?? [];
  const comWhats = numeros.filter((n) => n.whatsapp === true).length;
  const fora = data?.fora;
  const textoFora = [
    fora?.fixos ? `${fora.fixos} ${fora.fixos === 1 ? 'fixo' : 'fixos'} (não ligamos para fixo)` : '',
    fora?.da_equipe ? `${fora.da_equipe} ${fora.da_equipe === 1 ? 'número' : 'números'} da equipe` : '',
    fora?.nao_e_daqui ? `${fora.nao_e_daqui} marcado${fora.nao_e_daqui === 1 ? '' : 's'} "não é daqui"` : '',
  ].filter(Boolean).join(' · ');

  return (
    <section className={`mt-3 overflow-hidden ${CARD}`} aria-label="Números para ligar">
      <header className="flex h-[30px] items-center justify-between gap-2 border-b border-border bg-[#FAF8F4] px-3.5 dark:bg-[#241A1A]">
        <span className="text-[10px] font-bold uppercase tracking-[1.2px] text-card-foreground">Números para ligar</span>
        <span className="truncate text-[10.5px] text-muted-foreground">
          {isLoading ? 'procurando…' : `${numeros.length} ${numeros.length === 1 ? 'celular' : 'celulares'} · ${comWhats} com WhatsApp`}
        </span>
      </header>

      {isLoading ? (
        <p className="px-3.5 py-3 text-[12px] text-muted-foreground">Juntando os números do cliente e conferindo quem tem WhatsApp…</p>
      ) : isError ? (
        <p className="px-3.5 py-3 text-[12px] text-[#7A2235] dark:text-[#C87484]">
          {(error as Error)?.message || 'Não foi possível buscar os números.'}{' '}
          <button type="button" onClick={() => refetch()} className="font-semibold underline">Tentar de novo</button>
        </p>
      ) : numeros.length === 0 ? (
        <p className="px-3.5 py-3 text-[12px] text-muted-foreground">Nenhum celular achado para este cliente.</p>
      ) : (
        <ul>
          {numeros.map((n) => {
            const op = operadora(n.operadora);
            const tentado = !!n.ultima_ligacao;
            return (
              <li key={n.telefone} className="grid h-[52px] grid-cols-[10px_minmax(0,1fr)_auto_28px_28px] items-center gap-2 border-b border-[#EDE8E0] px-3 last:border-b-0 dark:border-[#332727]">
                <span
                  aria-hidden
                  className={`h-2 w-2 rounded-full ${tentado ? 'bg-[#B4808C]' : 'border-[1.5px] border-[#A6907B]'}`}
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-display text-[13.5px] font-semibold tabular-nums text-foreground">{fmt(n.telefone)}</span>
                    {op && (
                      <span className="inline-flex h-[17px] items-center rounded-[5px] border border-border bg-card px-1.5 text-[9.5px] font-bold text-foreground">{op}</span>
                    )}
                    {n.whatsapp === true && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#075E54] dark:text-[#25D366]">
                        <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[#128C7E] dark:bg-[#25D366]" />WhatsApp
                      </span>
                    )}
                  </div>
                  <div className="flex min-w-0 items-center gap-1.5">
                    {n.quarentena_ate && (
                      <span className={`inline-flex h-4 flex-none items-center rounded-[5px] px-1.5 text-[9.5px] font-bold text-white ${HACHURA}`}>
                        Quarentena até {diaMes(n.quarentena_ate)}
                      </span>
                    )}
                    {n.nao_perturbe && (
                      <span className={`inline-flex h-4 flex-none items-center rounded-[5px] px-1.5 text-[9.5px] font-bold text-white ${HACHURA}`}>
                        Não perturbe
                      </span>
                    )}
                    <span className="truncate text-[11px] text-muted-foreground" title={n.origens.join(' · ')}>{n.rotulo}</span>
                  </div>
                </div>
                <span className={`whitespace-nowrap text-right text-[10.5px] font-semibold ${tentado ? 'text-[#7A2235] dark:text-[#C87484]' : 'text-muted-foreground'}`}>
                  {ultimaVez(n)}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    if (!adAberto) { onLigar(n.telefone); return; }
                    // Com o Auto Dialer aberto o softphone está com a sala: liga por ela, pela Linx.
                    ligarOutroNumeroAutoDialer(n.telefone, leadId)
                      .then(() => toast.success(`Ligando para ${fmt(n.telefone)} pela Linx, na sala do Auto Dialer`))
                      .catch((e: Error) => toast.error('Não ligou', { description: e.message }));
                  }}
                  title={adAberto ? 'Ligar para este número pela sala do Auto Dialer (Linx)' : 'Ligar só para este número (softphone, como hoje)'}
                  aria-label={`Ligar para ${fmt(n.telefone)}`}
                  className="flex h-7 w-7 items-center justify-center rounded-[7px] border border-border bg-card text-[#5B0A0D] hover:bg-[#F4F0EA] dark:text-[#C87484] dark:hover:bg-[#2A1F1F]"
                >
                  <Phone className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => onContatoReal(n.telefone)}
                  title="É o contato real — leva o número para o Contato"
                  aria-label={`Usar ${fmt(n.telefone)} como contato real`}
                  className="flex h-7 w-7 items-center justify-center rounded-[7px] border border-border bg-card text-[#5B0A0D] hover:bg-[#F4F0EA] dark:text-[#C87484] dark:hover:bg-[#2A1F1F]"
                >
                  <UserCheck className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {!isLoading && textoFora && (
        <p className="border-t border-border px-3.5 py-2 text-[10.5px] text-muted-foreground">Ficam fora: {textoFora}.</p>
      )}

      {!isLoading && (
        <div className="border-t border-border px-3 py-2.5">
          {adAberto ? (
            <div className="flex h-10 items-center justify-center gap-2 rounded-[10px] border border-border bg-[#F4F0EA] text-[12.5px] font-bold text-[#5B0A0D] dark:bg-[#2A1F1F] dark:text-[#C87484]">
              <span aria-hidden className="h-2 w-2 rounded-full bg-[#5B0A0D] shadow-[0_0_0_3px_rgba(91,10,13,0.16)] dark:bg-[#C87484]" />
              Auto Dialer ligando · lead {Math.min(adAberto.totais.feitos + 1, adAberto.totais.leads)} de {adAberto.totais.leads}
            </div>
          ) : (
            <>
              <button
                type="button"
                disabled={iniciar.isPending}
                onClick={() => iniciar.mutate(fila)}
                className="flex h-10 w-full items-center justify-center gap-2 rounded-[10px] border-0 bg-[linear-gradient(150deg,#4A1320,#5B0A0D)] text-[12.5px] font-bold text-white transition hover:brightness-110 disabled:opacity-60 dark:bg-[linear-gradient(150deg,#9D2530,#6C2236)]"
              >
                <Phone className="h-3.5 w-3.5" />
                {iniciar.isPending ? 'Começando…' : numeros.length === 0 ? 'Procurar na Assertiva e ligar em sequência' : `Ligar em sequência · ${numeros.length} ${numeros.length === 1 ? 'celular' : 'celulares'}`}
              </button>
              <p className="mt-1.5 text-[10.5px] leading-snug text-muted-foreground">
                {depois > 0 ? `Depois segue para os próximos ${depois} ${depois === 1 ? 'lead' : 'leads'} desta lista. ` : ''}
                Pela rota classificada: você só ouve quando uma pessoa atender.
              </p>
            </>
          )}
        </div>
      )}
    </section>
  );
}
